/* eslint-disable sort-keys */
import React, { Component } from 'react';

import APIDocsCurl from 'common/developers/docs/api/APIDocsCurl';
import APIDocsMethod from 'common/developers/docs/api/APIDocsMethod';
import Highlight from 'common/developers/docs/api/Highlight';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

export default class APIDocsListPostsBlock extends Component {
  createdAt = new Date();

  render() {
    return (
      <APIDocsBlock>
        <div>
          <div className="heading">List posts</div>
          <div className="text">
            Returns a list of posts. Include parameters to specify board, pagination, filtering, and
            sorting.
          </div>
          <APIDocsTable
            title="Arguments"
            rows={[
              {
                heading: 'apiKey',
                subheading: 'string',
                description: 'Your secret API key.',
              },
              {
                heading: 'boardID',
                subheading: 'string (optional)',
                description: "The id of the board you'd like to fetch posts for.",
              },
              {
                heading: 'authorID',
                subheading: 'string (optional)',
                description: 'If specified, will only fetch posts by the author with this id.',
              },
              {
                heading: 'companyID',
                subheading: 'string (optional)',
                description:
                  'If specified, will only fetch posts created by users linked to the company with this custom identifier.',
              },
              {
                heading: 'tagIDs',
                subheading: 'array (optional)',
                description:
                  'If specified, will only fetch posts tagged with at least one of the tags in the array.',
              },
              {
                heading: 'limit',
                subheading: 'number (optional)',
                description:
                  "The number of posts you'd like to fetch. Defaults to 10 if not specified.",
              },
              {
                heading: 'search',
                subheading: 'string (optional)',
                description: 'If specified, will only fetch posts that match your search query.',
              },
              {
                heading: 'skip',
                subheading: 'number (optional)',
                description:
                  "The number of posts you'd like to skip before starting to fetch. Defaults to 0 if not specified.",
              },
              {
                heading: 'sort',
                subheading: 'string (optional)',
                description:
                  'The order in which the posts should be fetched. Options include: "newest", "oldest", "relevance", "score", "statusChanged", "trending". Defaults to "newest" if not specified. The "relevance" sort can only be specified if a search value has been specified.',
              },
              {
                heading: 'status',
                subheading: 'string (optional)',
                description:
                  'A comma separated list of statuses. Only posts with these statuses will be fetched.',
              },
            ]}
          />
          <div className="subheading">Returns</div>
          <div className="text">
            A dictionary with a "posts" property that contains an array of post objects. There's
            also a "hasMore" property that specifies whether this query returns more posts than the
            limit.
          </div>
        </div>
        <div>
          <div className="text">Endpoint</div>
          <Highlight lang="http">https://canny.io/api/v1/posts/list</Highlight>
          <APIDocsMethod />
          <APIDocsCurl
            path="https://canny.io/api/v1/posts/list"
            body={`\\
    -d apiKey=YOUR_API_KEY \\
    -d boardID=553c3ef8b8cdcd1501ba1234`}
          />
          <div className="text">Example Response</div>
          <Highlight lang="json">
            {`{
  "hasMore": true,
  "posts": [{
    "id": "553c3ef8b8cdcd1501ba1238",
    "author": {
      "id": "553c3ef8b8cdcd1501ba123a",
      "created": "${this.createdAt.toISOString()}",
      "email": "test@test.test",
      "isAdmin": false,
      "name": "Sally Doe",
      "url": "https://your-company.canny.io/admin/users/sally-doe",
      "userID": "1234"
    },
    "board": {
      "created": "${this.createdAt.toISOString()}",
      "id": "553c3ef8b8cdcd1501ba1234",
      "name": "Feature Requests",
      "postCount": 123,
      "url": "https://your-company.canny.io/admin/board/feature-requests"
    },
    "by": {
      "id": "524c3ef8b8cdcd1501ba246b",
      "created": "${this.createdAt.toISOString()}",
      "email": "test@john.test",
      "isAdmin": true,
      "name": "John Doe",
      "url": "https://your-company.canny.io/admin/users/john-doe",
      "userID": "5678"
    },
    "category": {
      "id": "553c3ef8b8cdcd1501ba2234",
      "name": "Dashboard",
      "parentID": null,
      "postCount": 42,
      "url": "https://your-company.canny.io/admin/board/feature-requests?category=dashboard"
    },
    "clickup": {
      "linkedTasks": [{
        "id": "123",
        "linkID": "2334jdsai23234io22",
        "name": "Clickup issue",
        "postID": "553c3ef8b8cdcd1501ba1238",
        "status": "to do",
        "url": "https://app.clickup.com/t/123456"
      }]
    },
    "commentCount": 10,
    "created": "${this.createdAt.toISOString()}",
    "customFields": [{
      "id": "553c3ef8b8cdcd1501ba2238",
      "name": "priority",
      "value": "high",
    }],
    "details": "Test post details",
    "eta": "February 2020",
    "imageURLs": [
      "https://canny.io/images/93fc5808937760b82c3dc00aa5cd86b8.png",
      "https://canny.io/images/316e5600645b81e4be287a52d506dbfd.jpg"
    ],
    "jira": {
      "linkedIssues": [{
        "id": "123",
        "key": "ID-123",
        "url": "https://your-company.atlassian.net/browse/ID-123"
      }]
    },
    "linear": {
      "linkedIssueIDs": ["ID-123", "ID-345"],
    },
    "mergeHistory":[{
      "created":"${this.createdAt.toISOString()}",
      "post": {
        "created":"${this.createdAt.toISOString()}",
        "details":"Awesome feature post details",
        "id": "553c3ef8b8cdcd1501ba6789",
        "imageURLs":[],
        "title":"Another awesome feature request"
      }
    }],
    "owner": {
      "id": "553c3ef8b8cdcd1501ba123a",
      "created": "${this.createdAt.toISOString()}",
      "email": "test@test.test",
      "isAdmin": true,
      "name": "Sally Doe",
      "url": "https://your-company.canny.io/admin/users/sally-doe",
      "userID": "1234"
    },
    "score": 72,
    "status": "in progress",
    "statusChangedAt": "${this.createdAt.toISOString()}",
    "tags": [{
      "id": "553c3ef8b8cdcd1501ba3234",
      "name": "iOS",
      "postCount": 15,
      "url": "https://your-company.canny.io/admin/board/feature-requests?tags=ios"
    }],
    "title": "An awesome feature request",
    "url": "https://your-company.canny.io/admin/board/feature-requests/p/post-title"
  }]
}`}
          </Highlight>
        </div>
      </APIDocsBlock>
    );
  }
}
